import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, Send, Loader2, X, User, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServiceChat({ requestId, otherPartyName, getAuthHeaders, userType = 'customer' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      fetchMessages();
      // Poll for new messages every 3 seconds when chat is open
      pollInterval.current = setInterval(fetchMessages, 3000);
    } else {
      // Check unread count when closed
      checkUnread();
      const unreadInterval = setInterval(checkUnread, 10000);
      return () => clearInterval(unreadInterval);
    }
    
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [isOpen, requestId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(
        `${API}/services/${requestId}/chat`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setMessages(response.data);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkUnread = async () => {
    try {
      const response = await axios.get(
        `${API}/services/${requestId}/chat/unread`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      // Silently fail
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Optimistically add message
    const tempMessage = {
      message_id: `temp_${Date.now()}`,
      content: messageContent,
      sender_type: userType,
      sender_name: 'You',
      created_at: new Date().toISOString(),
      pending: true
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      await axios.post(
        `${API}/services/${requestId}/chat`,
        { content: messageContent },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
      // Remove optimistic message
      setMessages(prev => prev.filter(m => m.message_id !== tempMessage.message_id));
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <>
      {/* Chat Toggle Button */}
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="w-full bg-blue-600 hover:bg-blue-700 relative"
          data-testid="open-service-chat"
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          Chat with {userType === 'customer' ? 'Mechanic' : 'Customer'}
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </Button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <Card className="mt-4" data-testid="service-chat-window">
          <CardHeader className="py-3 px-4 bg-blue-600 text-white rounded-t-lg flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
              <div>
                <CardTitle className="text-sm font-medium text-white">{otherPartyName}</CardTitle>
                <p className="text-xs text-blue-100">
                  {userType === 'customer' ? 'Your Mechanic' : 'Customer'}
                </p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white/20 rounded"
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          </CardHeader>

          <CardContent className="p-0">
            {/* Messages Area */}
            <div className="h-64 overflow-y-auto p-4 bg-gray-50">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No messages yet</p>
                  <p className="text-xs">Start the conversation!</p>
                </div>
              ) : (
                Object.entries(groupedMessages).map(([date, dateMessages]) => (
                  <div key={date}>
                    <div className="flex justify-center my-3">
                      <span className="px-3 py-1 bg-gray-200 rounded-full text-xs text-gray-600">
                        {date}
                      </span>
                    </div>
                    {dateMessages.map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`flex mb-3 ${
                          msg.sender_type === userType ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`max-w-[75%] px-3 py-2 rounded-2xl ${
                            msg.sender_type === userType
                              ? 'bg-blue-600 text-white rounded-br-sm'
                              : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100'
                          } ${msg.pending ? 'opacity-70' : ''}`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            msg.sender_type === userType ? 'text-blue-100' : 'text-gray-400'
                          }`}>
                            {formatTime(msg.created_at)}
                            {msg.pending && ' • Sending...'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <form onSubmit={sendMessage} className="p-3 border-t bg-white rounded-b-lg">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1"
                  disabled={sending}
                  data-testid="chat-message-input"
                />
                <Button 
                  type="submit" 
                  size="icon" 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={sending || !newMessage.trim()}
                  data-testid="send-chat-message"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </>
  );
}
